import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Info, FileText, Shield } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0d0e12] text-white flex flex-col font-sans selection:bg-[#FF5C00]/30">
      {/* Header */}
      <header className="py-6 px-4 md:px-8 max-w-7xl mx-auto w-full flex items-center justify-between z-10 relative">
        <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/')}>
          <Logo />
        </div>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-sm font-bold text-gray-400 hover:text-white transition-colors"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Retour
        </button>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 md:px-8 py-12">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center justify-center p-3 bg-[#FF5C00]/10 rounded-full mb-4">
            <FileText className="w-8 h-8 text-[#FF5C00]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Conditions Générales</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Veuillez lire attentivement nos conditions générales de vente (CGV) et d'utilisation (CGU).
          </p>
        </div>

        <div className="space-y-12">
          {/* CGU Section */}
          <section className="bg-[#121212] border border-white/10 rounded-3xl p-8 md:p-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <Info className="w-6 h-6 text-[#FF5C00]" />
              Conditions Générales d'Utilisation (CGU)
            </h2>
            <div className="space-y-6 text-gray-300 leading-relaxed">
              <p>
                Les présentes Conditions Générales d'Utilisation réglementent l'accès et l'utilisation de la plateforme Festin.
                En accédant à la plateforme, vous acceptez pleinement et sans réserve les présentes conditions.
              </p>
              <div>
                <h3 className="text-white font-semibold mb-2">1. Accès au service</h3>
                <p>
                  L'accès à la plateforme est réservé aux personnes majeures ou mineures avec autorisation parentale. 
                  Nous nous réservons le droit de suspendre, d'interrompre ou de modifier l'accès au service pour des raisons 
                  de maintenance ou de mise à jour.
                </p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">2. Inscription et compte</h3>
                <p>
                  Pour utiliser nos services, vous devez créer un compte en fournissant des informations exactes. Vous êtes 
                  responsable de la confidentialité de vos identifiants et de toutes les actions effectuées sous votre compte.
                </p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">3. Comportement des utilisateurs</h3>
                <p>
                  Les utilisateurs s'engagent à ne pas utiliser la plateforme à des fins illégales, pour diffuser des contenus 
                  offensants, ou pour perturber le bon fonctionnement du site.
                </p>
              </div>
            </div>
          </section>

          {/* CGV Section */}
          <section className="bg-[#121212] border border-white/10 rounded-3xl p-8 md:p-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#4b3cfa]" />
              Conditions Générales de Vente (CGV)
            </h2>
            <div className="space-y-6 text-gray-300 leading-relaxed">
              <p>
                Ces Conditions Générales de Vente s'appliquent à toutes les transactions réalisées sur la plateforme Festin
                (réservations d'emplacements, frais de service, reversements aux organisateurs).
              </p>

              <div>
                <h3 className="text-white font-semibold mb-2">1. Rôle de Festin et facilitation de paiement</h3>
                <p>
                  Festin agit en qualité d'intermédiaire technique et de <strong className="text-white">facilitateur de paiement</strong> entre
                  les organisateurs de festivals et les exposants. Les paiements sont opérés via notre prestataire de services de
                  paiement <strong className="text-white">Stripe</strong> (Stripe Payments Europe Ltd), y compris sa solution
                  Stripe Connect pour le reversement des fonds aux organisateurs. En utilisant la plateforme, l'utilisateur
                  accepte les conditions d'utilisation de Stripe. Festin n'exerce pas d'activité d'établissement de paiement au
                  sens réglementaire : la garde et le mouvement des fonds sont assurés par Stripe.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">2. Réservation, prix et frais de service</h3>
                <p>
                  Les réservations de stands sont fermes une fois le paiement confirmé. Les tarifs sont indiqués en euros.
                  Le montant réglé par l'exposant se compose, le cas échéant, de deux éléments distincts : (i) le
                  <strong className="text-white"> droit d'entrée</strong> fixé par l'organisateur, qui lui revient, et (ii) les
                  <strong className="text-white"> frais de service Festin</strong> (forfait ou pourcentage selon la grille tarifaire
                  en vigueur), qui rémunèrent la mise en relation et l'usage de la plateforme. Ces montants sont affichés en toute
                  transparence avant paiement.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">3. Encaissement et reversement aux organisateurs</h3>
                <div className="space-y-2">
                  <p>
                    Deux modalités de reversement du droit d'entrée s'appliquent selon la situation de l'organisateur :
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>
                      <strong className="text-white">Reversement automatique</strong> — lorsque l'organisateur a connecté son compte
                      de paiement (Stripe Connect), le droit d'entrée lui est transféré automatiquement par Stripe lors du paiement,
                      Festin ne conservant que ses frais de service.
                    </li>
                    <li>
                      <strong className="text-white">Détention temporaire</strong> — lorsque l'organisateur n'a pas encore connecté
                      son compte, le droit d'entrée est encaissé à titre transitoire pour son compte et lui est
                      <strong className="text-white"> reversé dès l'activation de son compte de paiement</strong>. Ces sommes sont
                      détenues de manière séparée et n'appartiennent pas à Festin ; elles restent dues à l'organisateur.
                    </li>
                  </ul>
                  <p>
                    L'organisateur est seul responsable de l'exactitude des informations fournies lors de son inscription au
                    service de paiement (identité, coordonnées bancaires) et des obligations fiscales et sociales afférentes aux
                    sommes qu'il perçoit.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">4. Programme d'apporteurs d'affaires (Promoteurs)</h3>
                <p>
                  Festin propose un programme d'affiliation permettant à des apporteurs d'affaires (« Promoteurs ») de
                  recommander des organisateurs. Le Promoteur validé perçoit une <strong className="text-white">commission de 20%
                  des frais de service nets encaissés par Festin</strong> sur les festivals qu'il a apportés, sur une période de
                  12 mois. Le versement intervient dès que le solde dû atteint le seuil de 150 € et sur émission d'une facture par
                  le Promoteur. Toute commission liée à une transaction remboursée dans les 30 jours est annulée. Les modalités
                  complètes figurent au contrat d'apporteur d'affaires accepté par le Promoteur.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">5. Programme de parrainage entre exposants</h3>
                <div className="space-y-2">
                  <p>
                    Festin propose un programme de parrainage permettant à un exposant (« Parrain ») d'inviter d'autres
                    exposants (« Filleuls ») via un lien personnel. Ce programme est distinct du programme d'apporteurs
                    d'affaires (Promoteurs) décrit à l'article&nbsp;4.
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>
                      <strong className="text-white">Avantage Filleul</strong> — une réduction de bienvenue de 10&nbsp;€ est
                      appliquée sur les frais de service Festin de sa première candidature, à condition de s'être inscrit via
                      un lien de parrainage valide. Cette réduction est utilisable une seule fois.
                    </li>
                    <li>
                      <strong className="text-white">Avantage Parrain</strong> — un crédit de 10&nbsp;€ est accordé au Parrain
                      pour chaque Filleul dont une candidature devient définitivement confirmée après paiement, dans la limite
                      d'un plafond cumulé de <strong className="text-white">50&nbsp;€</strong> par compte. Le crédit est acquis
                      une seule fois par Filleul.
                    </li>
                  </ul>
                  <p>
                    Le crédit constitue un <strong className="text-white">avoir déductible uniquement des frais de service
                    Festin</strong> lors des paiements ultérieurs du Parrain. Il n'est ni convertible en espèces, ni
                    remboursable, ni cessible, et ne s'applique pas au droit d'entrée reversé à l'organisateur. L'avantage n'est
                    déclenché qu'au paiement effectivement confirmé du Filleul, jamais avant.
                  </p>
                  <p>
                    Le Parrain et le Filleul doivent être des comptes et des personnes distincts. Festin se réserve le droit
                    d'annuler tout crédit ou réduction obtenu par fraude, abus, auto-parrainage ou création de faux comptes, et
                    de <strong className="text-white">modifier, suspendre ou clôturer le programme à tout moment</strong>, sans
                    que cela ouvre droit à indemnité. Les offres promotionnelles ou de lancement (tarifs réduits, crédits
                    exceptionnels) sont temporaires et soumises aux présentes conditions.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">6. Annulation et Remboursement</h3>
                <div className="space-y-2">
                  <p>Toute annulation doit être notifiée par écrit. Les remboursements seront traités selon le barème suivant :</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Plus de 60 jours avant l'événement : 100% de remboursement.</li>
                    <li>Entre 30 et 60 jours : 50% de remboursement.</li>
                    <li>Moins de 30 jours : aucun remboursement.</li>
                  </ul>
                  <p>
                    En cas de remboursement, le reversement éventuellement transféré à l'organisateur est repris selon les mêmes
                    proportions, et les frais de service Festin peuvent être conservés au titre du service déjà rendu.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-2">7. Responsabilités</h3>
                <p>
                  Festin agit comme intermédiaire technique et facilitateur de paiement. Nous ne saurions être tenus responsables
                  de l'annulation d'un festival, de la non-conformité d'un stand ou de l'inexécution des obligations d'une partie.
                  La relation commerciale lie directement l'organisateur et l'exposant.
                </p>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-16 text-center text-sm text-gray-500">
          <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
          <p className="mt-2">Pour toute question, veuillez nous contacter via la section Aide & Support.</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
